import {
  Button,
  EmptyNote,
  PanelError,
  SkeletonStack,
} from "@scribenote/brian-design-system";

import { PolicyTrace } from "./PolicyTrace";
import { clinicTime, duration, readableEvidence, words } from "./traceFormat";
import { useTraceDetail } from "./traceQueries";

export const TraceDetail = ({
  eventUuid,
  timezone,
  active,
  onOpenEvent,
}: {
  eventUuid: string;
  timezone: string;
  active: boolean;
  onOpenEvent: (eventUuid: string) => void;
}) => {
  const query = useTraceDetail(eventUuid, active);
  if (query.isPending) return <SkeletonStack count={3} className="p-5" />;
  if (query.isError)
    return (
      <PanelError
        label="this trace"
        onRetry={() => void query.refetch()}
        className="min-h-40"
      />
    );
  const trace = query.data;
  return (
    <div
      className="space-y-4 border-t border-rule bg-wall p-4 sm:p-5"
      data-dd-privacy="hidden"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-annotation">
        <span className="font-mono">Event {eventUuid}</span>
        <span className="font-mono">
          Observed elapsed: {duration(trace.summary.elapsed_ms)}
        </span>
      </div>
      {trace.consumer_run && (
        <div className="space-y-1 font-mono text-xs text-annotation">
          <p>
            Consumer: {words(trace.consumer_run.state)} ·{" "}
            {trace.consumer_run.attempts} of {trace.consumer_run.attempt_budget}{" "}
            attempts
          </p>
          {trace.consumer_run.last_error && (
            <p className="text-ink-body">
              Last recorded error: {trace.consumer_run.last_error}
            </p>
          )}
          {!["done", "exhausted", "parked"].includes(
            trace.consumer_run.state,
          ) && (
            <p>
              Next scheduled attempt:{" "}
              {clinicTime(
                trace.consumer_run.next_attempt_at,
                trace.clinic_timezone || timezone,
              )}
            </p>
          )}
        </div>
      )}
      <p className="font-mono text-xs text-annotation">
        Event time:{" "}
        {clinicTime(
          trace.summary.occurred_at,
          trace.clinic_timezone || timezone,
        )}
      </p>
      <details className="rounded-paper border border-rule bg-card p-3 text-xs text-annotation">
        <summary className="cursor-pointer font-medium text-ink-body">
          Recorded event payload
        </summary>
        <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-all font-mono text-xs text-ink-body">
          {readableEvidence(trace.event.payload)}
        </pre>
      </details>
      {trace.decision ? (
        <PolicyTrace trace={trace} />
      ) : (
        <EmptyNote>Decision has not been recorded for this event.</EmptyNote>
      )}
      {(trace.links.parent_event_uuid ||
        trace.links.child_event_uuids.length > 0) && (
        <section className="border-t border-rule pt-3 text-sm">
          <p className="text-mono-caps text-xs text-annotation">
            Linked events
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {trace.links.parent_event_uuid && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onOpenEvent(trace.links.parent_event_uuid!)}
              >
                Parent event
              </Button>
            )}
            {trace.links.child_event_uuids.map((uuid, index) => (
              <Button
                key={uuid}
                size="sm"
                variant="outline"
                onClick={() => onOpenEvent(uuid)}
              >
                Child event {index + 1}
              </Button>
            ))}
          </div>
          <p className="mt-2 text-xs text-annotation">
            Each linked event has its own trace.
          </p>
        </section>
      )}
    </div>
  );
};
